import { useQueryClient } from '@tanstack/react-query';
import { useEffect, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { ApiError, onPasswordChangeRequired, onSessionLost } from '../lib/api';
import { cachedMe, isOfflineError, qk, useMe } from '../lib/queries';
import { Icon } from '../ui/Icon';
import { ForcedPasswordChange } from './ChangePassword';
import { canUse, homeFor } from './roles';

/**
 * The app, with a bar saying it is running on the identity this phone remembers rather than one
 * the server has just confirmed. It is not a scan-queue message — the queue has its own pill and
 * says its own truth; this one is only about who the app thinks you are.
 *
 * The wrapper is always here, banner or no banner. Swapping it in and out around `children` would
 * remount everything under it the moment the signal came back: a new queue, and a Receive screen
 * whose counter drops to zero in the middle of a delivery.
 */
function AppShell({ offline, children }: { offline: boolean; children: ReactNode }) {
  const { refetch } = useMe();
  // `refetchOnReconnect` covers a phone whose Wi-Fi actually dropped. A server that was simply
  // unreachable never flips `navigator.onLine`, so nothing would ask again and the bar would
  // outlive the outage — which is its own small lie. While it is up, we keep asking.
  useEffect(() => {
    if (!offline) return;
    const t = setInterval(() => void refetch(), 15_000);
    return () => clearInterval(t);
  }, [offline, refetch]);

  return (
    <div className={offline ? 'offline-shell' : undefined}>
      <p className="offline-bar" role="status" hidden={!offline}>
        <Icon name="wifi-off" size={18} />
        Working offline · scans still queue
      </p>
      {children}
    </div>
  );
}

export function RequireAuth({ surface, children }: { surface: 'office' | 'scan'; children: ReactNode }) {
  const me = useMe();
  const loc = useLocation();
  const qc = useQueryClient();
  // Any request answered 401 asks `me` again at once. `me` stays the one that decides: its own 401
  // forgets the remembered identity and this component sends the tab to sign-in, just below.
  useEffect(() => onSessionLost(() => void qc.invalidateQueries({ queryKey: qk.me })), [qc]);
  // A request refused until this person chooses their own password asks `me` again too; its answer
  // brings up the change screen below. It is never a sign-out (api.ts, `onPasswordChangeRequired`).
  useEffect(() => onPasswordChangeRequired(() => void qc.invalidateQueries({ queryKey: qk.me })), [qc]);
  if (me.isPending) return <div className="boot" aria-busy="true" />;
  // No signal at the truck. Handing the worker a bare error would not only hide the queue, it
  // would stop them scanning at all — and it would keep `QueueProvider` unmounted, so nothing
  // already on disk would be retried either. No signal is exactly when this app is needed.
  const cached = me.error ? cachedMe() : null;
  const offline = Boolean(me.error) && isOfflineError(me.error) && cached !== null && canUse(cached.role, surface);
  if (me.error && !offline) {
    if (me.error instanceof ApiError && me.error.status === 401) return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
    return (
      <div role="alert" className="boot-error">
        {me.error.message}
      </div>
    );
  }
  if (!me.error && !canUse(me.data.role, surface)) return <Navigate to={homeFor(me.data.role)} replace />;
  // Still on a temporary password an admin was shown: this screen and nothing else, not even the shell
  // (the server would refuse it everything anyway). An identity remembered from before this field
  // existed has no such flag, and is not held.
  if ((me.error ? cached : me.data)?.mustChangePassword) return <ForcedPasswordChange surface={surface} />;
  return <AppShell offline={offline}>{children}</AppShell>;
}

export function HomeRedirect() {
  const me = useMe();
  if (me.isPending) return <div className="boot" aria-busy="true" />;
  if (me.error) {
    if (me.error instanceof ApiError && me.error.status === 401) return <Navigate to="/login" replace />;
    const cached = cachedMe();
    if (isOfflineError(me.error) && cached) return <Navigate to={homeFor(cached.role)} replace />;
    return (
      <div role="alert" className="boot-error">
        {me.error.message}
      </div>
    );
  }
  return <Navigate to={homeFor(me.data.role)} replace />;
}
